// Faithful JS mirror of the Ruby coercions in "The .to_i that ate the 0.5".
// Each function reproduces the leading-numeric parse of one coercion so the
// widget can show, side by side, which coercions silently reshape a value.
//
// Scope: non-negative decimal inputs (e.g. "3", "3.5", "46"). Junk input
// (e.g. "abc") is handled the way each function genuinely would: Ruby's
// String#to_i / String#to_f invent 0, while parseInt / parseFloat return NaN.

// Ruby String#to_i: skip leading whitespace, take an optional sign and the
// run of digits that follows, ignore everything after — truncating at the
// first non-digit, so "3.5" -> 3. No leading integer -> 0.
export function rubyToI(input) {
  const match = String(input).match(/^\s*[+-]?\d+/)
  return match ? parseInt(match[0], 10) : 0
}

// Ruby String#to_f: parse the leading float; junk -> 0.0.
export function rubyToF(input) {
  const match = String(input).match(/^\s*[+-]?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?/)
  return match ? parseFloat(match[0]) : 0
}

// Ruby Float#round(0): rounds half AWAY FROM ZERO (2.5 -> 3, -2.5 -> -3),
// unlike banker's rounding. Applied to String#to_f of the input.
export function rubyToFRound(input) {
  const value = rubyToF(input)
  return Math.sign(value) * Math.round(Math.abs(value))
}

// Ruby format("%g", value): the shorter of %e/%f at 6 significant digits,
// with trailing zeros stripped. Operates on the float value of the input.
export function formatG(input) {
  return gFormat(rubyToF(input), 6)
}

// JS parseInt / parseFloat, kept here so every coercion lives in one place.
export function jsParseInt(input) {
  return parseInt(String(input), 10)
}

export function jsParseFloat(input) {
  return parseFloat(String(input))
}

// Runs the input through every coercion and marks which cells diverge from
// the true value. The true value is the full-fidelity read (parseFloat). A
// cell diverges when it reports a different number — or, for junk input where
// the truth is NaN, when it invents a number instead of also reporting NaN.
export function coerce(input) {
  const trueValue = jsParseFloat(input)
  const cells = [
    numCell("to_i", ".to_i", rubyToI(input)),
    numCell("round", ".to_f.round(0)", rubyToFRound(input)),
    { key: "g", label: 'format("%g")', display: formatG(input), numeric: rubyToF(input) },
    numCell("pi", "parseInt", jsParseInt(input)),
    numCell("pf", "parseFloat", jsParseFloat(input))
  ]
  for (const cell of cells) {
    cell.diverges = diverges(cell.numeric, trueValue)
  }
  return { trueValue, trueDisplay: numDisplay(trueValue), cells }
}

// ---- internals ----

function numCell(key, label, numeric) {
  return { key, label, display: numDisplay(numeric), numeric }
}

function numDisplay(n) {
  return Number.isNaN(n) ? "NaN" : String(n)
}

function diverges(cellValue, trueValue) {
  if (Number.isNaN(trueValue)) return !Number.isNaN(cellValue)
  if (Number.isNaN(cellValue)) return true
  return Math.abs(cellValue - trueValue) > 1e-9
}

// Mirror of C printf %g at the given precision (significant digits).
//
// Ruby's %g rounds the SHORTEST decimal representation of the double (not the
// exact stored value) to `precision` significant digits, half-to-even, and only
// THEN chooses fixed vs exponential notation from the post-rounding exponent.
// Two consequences the exact-double path gets wrong:
//   * A value that rounds up across an order-of-magnitude boundary must pick its
//     notation from the rounded exponent: 999999.5 -> "1e+06", not "1000000".
//   * Rounding follows the shortest repr, so 99999.95 (stored as 99999.94999…)
//     rounds to "100000", where rounding the exact double would give "99999.9".
// toExponential() with no argument yields that shortest normalized repr, whose
// digits we round ourselves to stay faithful to Ruby.
function gFormat(value, precision) {
  if (!isFinite(value)) return String(value)
  if (value === 0) return "0"

  const p = precision < 1 ? 1 : precision
  const sign = value < 0 ? "-" : ""

  const normalized = Math.abs(value).toExponential() // e.g. "9.999995e+4"
  const eIndex = normalized.indexOf("e")
  const baseExp = parseInt(normalized.slice(eIndex + 1), 10)
  const mantissaDigits = normalized.slice(0, eIndex).replace(".", "")

  const { digits, exp } = roundSignificant(mantissaDigits, baseExp, p)

  if (exp < -4 || exp >= p) {
    return sign + expNotation(digits, exp)
  }
  return sign + fixedNotation(digits, exp)
}

// Rounds a normalized significant-digit string to `p` digits, half-to-even,
// returning the surviving digits and the (possibly bumped) exponent of the
// leading digit.
function roundSignificant(digitStr, baseExp, p) {
  if (digitStr.length <= p) {
    return { digits: digitStr, exp: baseExp }
  }

  const kept = digitStr.slice(0, p)
  const dropped = digitStr.slice(p)
  let roundUp = false
  const firstDropped = Number(dropped[0])
  if (firstDropped > 5) {
    roundUp = true
  } else if (firstDropped === 5) {
    // Exact tie only when nothing nonzero follows; then round to even.
    roundUp = /[1-9]/.test(dropped.slice(1)) || Number(kept[p - 1]) % 2 === 1
  }

  if (!roundUp) {
    return { digits: kept, exp: baseExp }
  }

  const incremented = incrementDigits(kept)
  if (incremented.length > kept.length) {
    // Carry grew the string (e.g. "999999" -> "1000000"); drop the new trailing
    // zero to stay at p significant digits and bump the exponent by one.
    return { digits: incremented.slice(0, p), exp: baseExp + 1 }
  }
  return { digits: incremented, exp: baseExp }
}

// Adds one to a string of decimal digits, propagating carries.
function incrementDigits(digitStr) {
  const chars = digitStr.split("")
  let i = chars.length - 1
  while (i >= 0 && chars[i] === "9") {
    chars[i] = "0"
    i -= 1
  }
  if (i < 0) {
    chars.unshift("1")
  } else {
    chars[i] = String(Number(chars[i]) + 1)
  }
  return chars.join("")
}

// Places the decimal point for fixed notation and strips trailing zeros.
function fixedNotation(digits, exp) {
  let s
  if (exp >= 0) {
    if (digits.length <= exp + 1) {
      s = digits + "0".repeat(exp + 1 - digits.length)
    } else {
      s = digits.slice(0, exp + 1) + "." + digits.slice(exp + 1)
    }
  } else {
    s = "0." + "0".repeat(-exp - 1) + digits
  }
  if (s.indexOf(".") !== -1) s = s.replace(/\.?0+$/, "")
  return s
}

// Renders exponential notation (d.ddde±NN) with a stripped mantissa and a
// zero-padded, minimum-two-digit exponent.
function expNotation(digits, exp) {
  const frac = digits.slice(1).replace(/0+$/, "")
  const mantissa = frac ? `${digits[0]}.${frac}` : digits[0]
  const expSign = exp < 0 ? "-" : "+"
  let e = Math.abs(exp).toString()
  if (e.length < 2) e = `0${e}`
  return `${mantissa}e${expSign}${e}`
}
